import {EventEmitter, Injectable} from '@angular/core';
import {Questionnaire, Translation} from '@ngmedax/common-questionnaire-types';
import {QuestionnaireVariablesService} from './questionnaire-variables.service';
import {QuestionnaireStateService} from './questionnaire-state.service';
import {SettingsService} from './settings.service';
import {CustomFieldDefinition, CustomFieldType} from '../../types';
import { format } from 'path';


interface ConditionSelect {
  questions: ConditionSelectQuestion[];
  questionToAnswerMap: {[pathHash: string]: ConditionSelectAnswer};
  answerToQuestionMap: {[pathHash: string]: ConditionSelectAnswer};
  variables: string[];
}

interface ConditionSelectQuestion {
  pathHash: string;
  title: Translation;
}

interface ConditionSelectAnswer {
  pathHash: string;
  title: Translation;
}

@Injectable()
export class QuestionnaireConditionService {
  public onChange: EventEmitter<void> = new EventEmitter<void>();
  public onUpdateSelect: EventEmitter<void> = new EventEmitter<void>();
  private select: ConditionSelect = null;

  /**
   * Hardcoded client (patient) variables
   */
  private clientVariables = [
    'client.age',
    'client.ageInMonths',
    'client.birthDate',
    'client.isMale',
    'client.isFemale',
    'client.isDiverse',
    'client.caseNr',
    'client.customerNr',
    'client.zipCode',
    'client.city',
    'client.street',
    'client.streetNr',
    // legacy
    'client.location',
    'client.status',
    'client.lastName',
    'client.firstName',
    'client.anonymous',
    'client.pseudonymous'
  ];

  /**
   * Hardcoded type mappings
   */
  private variableTypeMappings = {
    'client.age': 'numeric',
    'client.ageInMonths': 'numeric',
    'client.birthDate': 'date',
    'client.isMale': 'boolean',
    'client.isFemale': 'boolean',
    'client.isDiverse': 'boolean',
    'client.caseNr': 'numeric',
    'client.customerNr': 'numeric',
    'client.zipCode': 'numeric',
    'client.city': 'text',
    'client.street': 'text',
    'client.streetNr': 'numeric',
    // legacy
    'client.location': 'text',
    'client.status': 'text',
    'client.lastName': 'text',
    'client.firstName': 'text',
    'client.anonymous': 'boolean',
    'client.pseudonymous': 'boolean'
  }

  private allowedQuestionFormat = [
    Questionnaire.Container.Format.SINGLE_CHOICE,
    Questionnaire.Container.Format.MULTIPLE_CHOICE,
    Questionnaire.Container.Format.MATRIX
  ];

  /**
   * Custom field definitions loaded from settings API.
   */
  private customFieldDefinitions: CustomFieldDefinition[] = [];

  public constructor(
    private variables: QuestionnaireVariablesService,
    private state: QuestionnaireStateService,
    private settings: SettingsService
  ) {
    this.state.onAddElement.subscribe(() => this.updateSelect());
    this.state.onUpdateElement.subscribe(() => this.updateSelect());
    this.state.onDeleteElement.subscribe(() => this.updateSelect());
  }

  /**
   * Initializes custom field definitions from settings API.
   * Should be called once during editor initialization.
   */
  public async initCustomFields(): Promise<void> {
    this.customFieldDefinitions = await this.settings.loadCustomFieldDefinitions();
    this.updateSelect();
  }

  public getSelectData(useCached = true): ConditionSelect {
    (!this.select || !useCached) && this.updateSelect();
    return this.select;
  }

  /**
   * Returns container for variable including scope (e.g inline.foo)
   * @param variable
   */
  public getContainerForVariable(variable: string): Questionnaire.Container {
    if (!variable) {
      return;
    }

    if (this.variableTypeMappings[variable]) {
      return <any>{format: this.variableTypeMappings[variable]};
    }

    if (variable.match(/^inline\./)) {
      const variableName = variable.replace(/^inline\./g, '');
      const mappedPathHash = this.variables.getMappedPathHash('inline', variableName);
      const container = mappedPathHash ? this.state.getElement(mappedPathHash): undefined;
      return container;
    }

    if (variable.match(/^extern\./)) {
      const variableName = variable.replace(/^extern\./g, '');
      const data = <{format: string, default: string | number}>
        this.variables.getVariablesForScope('extern')[variableName] || {format: 'text', default: ''};

      const container = {id: '', format: data.format};
      return container;
    }

    if (variable.match(/^formula\./)) {
      return {id: '', format: 'numeric'};
    }

    // handle client.custom.* variables
    if (variable.match(/^client\.custom\./)) {
      const key = variable.replace(/^client\.custom\./g, '');
      const def = this.customFieldDefinitions.find(d => d.key === key);
      const format = this.mapCustomFieldTypeToFormat(def?.type);
      return <any>{format};
    }
  }

  /**
   * Maps custom field type to internal format type for condition expressions.
   *
   * @param type The custom field type
   * @returns The internal format type (text, numeric, date, boolean)
   */
  private mapCustomFieldTypeToFormat(type: CustomFieldType): string {
    const mapping: {[key: string]: string} = {
      'text': 'text',
      'number': 'numeric',
      'date': 'date',
      'select': 'text',
      'boolean': 'boolean'
    };
    return mapping[type] || 'text';
  }

  private updateSelect() {
    const questionnaire = this.state.getQuestionnaire();
    const questions: ConditionSelectQuestion[] = [];
    const questionToAnswerMap: any = {};
    const answerToQuestionMap: any = {};

    for (const question of questionnaire.questions) {
      if (!this.isAllowedQuestionFormat(question)) {
        continue;
      }

      const answers = this.getAnswers(question);

      (answers || []).map(answer => answerToQuestionMap[answer.pathHash] = question.pathHash);

      answers && (questionToAnswerMap[question.pathHash] = answers) && questions.push({
        title: question.title,
        pathHash: question.pathHash,
      });
    }

    // build custom field variables from loaded definitions
    const customVariables = this.customFieldDefinitions.map(def => `client.custom.${def.key}`);

    const variables = [
      ...this.variables.getVariableNamesForScope('inline').map(value => `inline.${value}`),
      ...this.variables.getVariableNamesForScope('scoring').map(value => `scoring.${value}`),
      ...this.variables.getVariableNamesForScope('extern').map(value => `extern.${value}`),
      ...this.variables.getVariableNamesForScope('formula').map(value => `formula.${value}`),
      ...this.clientVariables,
      ...customVariables
    ];

    this.select = {questions, questionToAnswerMap, answerToQuestionMap, variables};
    this.onUpdateSelect.emit();
  }

  private getAnswers(
    element: Questionnaire.Container
  ):  ConditionSelectAnswer[] {
    const walk = (el: Questionnaire.Container, title: Translation = null, answers: ConditionSelectAnswer[] = []) => {

      // concat titles e.g: el title > sub el title
      !el.omitContainer && title && Object.keys(el.title).forEach(
        locale => title[locale] && (title[locale] = `${title[locale]} > ${el.title[locale]}`));

      // init title if not already done. skip question titles
      !el.omitContainer && !title && !el.format && (title = this.clone(el.title));

      // recursively call for each sub element
      (el.elements || []).forEach((subElement) => walk(subElement, this.clone(title), answers));

      // otherwise add to answers array
      !el.elements && answers.push({title: title, pathHash: el.pathHash});

      return answers;
    };

    return walk(element);
  }

  private isAllowedQuestionFormat(container: Questionnaire.Container) {
    return this.allowedQuestionFormat.indexOf(container.format) !== -1;
  }

  private clone(obj: any) {
    return JSON.parse(JSON.stringify(obj));
  }
}
